import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  Search,
  Package,
  Calendar,
  Hash,
  DollarSign,
  FileText,
  Check,
  X,
  ScanLine,
} from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

interface Product {
  id: string;
  name: string;
  sku: string;
  brand_name?: string;
}

interface StockRecord {
  id: string;
  product_name: string;
  product_sku?: string;
  quantity: number;
  expiry_date?: string;
  batch_number?: string;
  observed_price?: number;
  notes?: string;
  created_at: string;
}

export default function StockCheck({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [observedPrice, setObservedPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Search products
  const { data: searchResults = [] } = useQuery<Product[]>({
    queryKey: ['products', 'search', searchTerm],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(searchTerm)}&limit=10`).then(res => res.data.data),
    enabled: searchTerm.length >= 2,
  });

  // Fetch existing stock records for this visit
  const { data: existingRecords = [] } = useQuery<StockRecord[]>({
    queryKey: ['stock-records', visitId],
    queryFn: () => api.get(`/stock-records?visit_id=${visitId}`).then(res => res.data.data),
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/stock-records', {
        visit_id: visitId,
        store_id: storeId,
        product_id: selectedProduct?.id,
        quantity: Number(quantity),
        expiry_date: expiryDate || undefined,
        batch_number: batchNumber || undefined,
        observed_price: observedPrice ? Number(observedPrice) : undefined,
        notes: notes || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      setSuccessMessage('Estoque registrado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['stock-records', visitId] });
      // Reset form
      setSearchTerm('');
      setSelectedProduct(null);
      setQuantity('');
      setExpiryDate('');
      setBatchNumber('');
      setObservedPrice('');
      setNotes('');
      setShowResults(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao registrar estoque');
    },
  });

  const handleSubmit = () => {
    if (!selectedProduct) {
      alert('Selecione um produto');
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      alert('Informe a quantidade');
      return;
    }
    submitMutation.mutate();
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
  };

  const handleBarcodeScan = async (barcode: string) => {
    setShowScanner(false);
    try {
      const res = await api.get(`/products/barcode/${encodeURIComponent(barcode)}`);
      handleSelectProduct(res.data.data);
    } catch {
      alert(`Produto não encontrado para código: ${barcode}`);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Verificar Estoque</h1>
          </div>
        </div>
      </header>

      {/* Success Toast */}
      {successMessage && (
        <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 p-4 space-y-6 pb-8">
        {/* Product Search */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Produto</h3>

          {selectedProduct ? (
            <div className="bg-white border border-teal-300 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-500 mt-1">SKU: {selectedProduct.sku}</p>
                  {selectedProduct.brand_name && (
                    <p className="text-sm text-gray-500">Marca: {selectedProduct.brand_name}</p>
                  )}
                </div>
                <button
                  onClick={handleClearProduct}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowResults(true);
                    }}
                    onFocus={() => setShowResults(true)}
                    placeholder="Buscar produto por nome ou SKU..."
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setShowScanner(true)}
                  className="flex items-center justify-center w-12 h-12 bg-teal-600 text-white rounded-xl active:bg-teal-700"
                  title="Escanear código de barras"
                >
                  <ScanLine className="h-5 w-5" />
                </button>
              </div>

              {/* Search Results Dropdown */}
              {showResults && searchTerm.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Nenhum produto encontrado
                    </div>
                  ) : (
                    searchResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          SKU: {product.sku}
                          {product.brand_name && ` | ${product.brand_name}`}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Fields (shown when product is selected) */}
        {selectedProduct && (
          <div className="space-y-4">
            {/* Quantity */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Package className="h-4 w-4 text-teal-600" />
                Quantidade *
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Ex: 24"
                min="0"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Data de Validade (opcional)
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Batch Number */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Hash className="h-4 w-4 text-teal-600" />
                Numero do Lote (opcional)
              </label>
              <input
                type="text"
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                placeholder="Ex: LOT-2026-001"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Observed Price */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <DollarSign className="h-4 w-4 text-teal-600" />
                Preco Observado (opcional)
              </label>
              <input
                type="number"
                value={observedPrice}
                onChange={(e) => setObservedPrice(e.target.value)}
                placeholder="Ex: 12.90"
                min="0"
                step="0.01"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FileText className="h-4 w-4 text-teal-600" />
                Observacoes (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Adicione observacoes sobre o estoque..."
                rows={3}
                className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitMutation.isPending || !selectedProduct || !quantity || Number(quantity) <= 0}
          className="w-full flex items-center justify-center gap-2 p-4 bg-teal-600 text-white rounded-xl font-semibold active:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Registrando...</span>
            </>
          ) : (
            <span>Registrar Estoque</span>
          )}
        </button>

        {/* Existing Stock Records List */}
        {existingRecords.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Registros de Estoque ({existingRecords.length})
            </h3>
            <div className="space-y-2">
              {existingRecords.map((record) => (
                <div
                  key={record.id}
                  className="bg-white rounded-xl border border-gray-100 p-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-teal-100 mt-0.5">
                      <Package className="h-4 w-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {record.product_name}
                      </p>
                      {record.product_sku && (
                        <p className="text-xs text-gray-500">SKU: {record.product_sku}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-2 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                          Qtd: {record.quantity}
                        </span>
                        {record.expiry_date && (
                          <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                            Val: {formatDate(record.expiry_date)}
                          </span>
                        )}
                        {record.batch_number && (
                          <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            Lote: {record.batch_number}
                          </span>
                        )}
                        {record.observed_price != null && (
                          <span className="inline-block px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                            R$ {Number(record.observed_price).toFixed(2)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(record.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {record.notes && (
                        <p className="text-xs text-gray-500 mt-1">{record.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
